import type {
  AIChatDTO,
  ConversationDetailDTO,
  QAQuestionDTO,
  QASearchResultDTO,
  RecordInfoDTO,
  ResearchSearchItem,
} from '@next-space/fe-api-idl';
import { ChatStatusType } from '@next-space/fe-api-idl';
import { API } from '../../api';
import { emitter, EmitterEvent } from '../../utils/emitter';
import { getDeepSearchContainer, getScrollChatList } from '../const';
import { checkLimit, fetchAiLimit } from './ai-limit-store';
import { getEditor } from './cache';
import { getQAQueryOption } from './hook';
import { v4 as uuid } from 'uuid';
import { ReferenceTools, type ReferenceData } from './reference-tools';
import { getI18n } from 'react-i18next';
import {
  addQuestion,
  conversationStore,
  getChatStatus,
  getConversation,
  getConversationEdit,
  setAiChatPages,
  setChatStatus,
  setConversationEdit,
  setUpdateChat,
  setUpdateConversation,
  setUpdateQuestion,
  updateNewQuestionIdAndChatId,
} from './store';
import { exchangeStreams, stopAllStream, streamAIResponse } from './stream';
import type { CreateChatParams } from './types';
import {
  AI_PAGE_ID,
  ChatStatus,
  getDefaultChat,
  getDefaultConversation,
  getDefaultConversationEditStore,
  getDefaultQuestion,
} from './types';
import { last, throttle } from 'lodash-es';
export const containerScrollToBottom = (id: string) => {
  if (ignoreScrollBottom) {
    return;
  }
  const container = document.getElementById(getScrollChatList(id));
  if (container) {
    const bottom = container.scrollHeight - container.clientHeight;
    if (bottom - container.scrollTop > 40) {
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    }
  }
  const conversation = getConversation(id);
  const chatId = last(last(conversation.questions)?.chatList)?.uuid;
  if (!chatId) {
    return;
  }
  const deepSearchContainer = document.getElementById(getDeepSearchContainer(chatId));
  if (deepSearchContainer) {
    const bottom = deepSearchContainer.scrollHeight - deepSearchContainer.clientHeight;
    if (bottom - deepSearchContainer.scrollTop > 40) {
      requestAnimationFrame(() => {
        deepSearchContainer.scrollTo({ top: deepSearchContainer.scrollHeight, behavior: 'smooth' });
      });
    }
  }
};
//如果用户手动滑到上面，则我们不需要自动滑到底部
let ignoreScrollBottom = false;
export const setIgnoreScrollBottom = (ignore: boolean) => {
  ignoreScrollBottom = ignore;
};

export class ConversationTools extends ReferenceTools {
  constructor(id: string) {
    super(id, (id: string, data: Record<string, any>) => setConversationEdit(id, data));
  }

  getConversation = () => {
    const conversation = conversationStore.getState()[this.id];
    return conversation || getDefaultConversation();
  };

  getEditor = () => {
    return getEditor(this.id);
  };

  getConversationEdit = () => {
    return getConversationEdit(this.id);
  };

  protected getData = (): ReferenceData => {
    const edit = this.getConversationEdit();
    const qaQueryOption = getQAQueryOption(edit.qaQueryOption);
    return {
      qaQueryOption,
      status: getChatStatus(this.id),
    };
  };

  setStatus = (status: ChatStatus) => {
    setChatStatus(this.id, status);
  };

  getQuestions = () => {
    return this.getConversation().questions || [];
  };

  setKeywords = (keywords: string) => {
    setConversationEdit(this.id, { keywords });
  };

  /** 设置聊天列表 */
  setQuestions = (questions: QAQuestionDTO[]) => {
    setUpdateConversation(this.id, { questions });
  };

  scrollToBottom = () => {
    containerScrollToBottom(this.id);
  };

  /** 清空聊天关键词和编辑器内容 */
  cleanConversationEditData = () => {
    setConversationEdit(this.id, { keywords: '' });

    this.setStatus(ChatStatus.DEFAULT);

    const editor = getEditor(this.id);

    if (editor) {
      editor.commands.setContent('');
      editor.commands.focus();
    }
  };

  /** 创建新的聊天会话 */
  createConversation = async (params: CreateChatParams) => {
    if (!checkLimit(params.spaceId)) {
      emitter.emit(EmitterEvent.chatAiLimit);
      throw new Error('ai limit exceeded');
    }

    if (this.id === AI_PAGE_ID) {
      this.cleanConversationEditData();
      this.setStatus(ChatStatus.LOADING);
    }
    try {
      const newConversation = await createNewConversation(params);
      this.cleanConversationEditData();

      // 异步创建消息
      const conversationTools = new ConversationTools(newConversation.id);
      conversationTools.cleanConversationEditData();
      void conversationTools.createQuestion(params);

      // 这里需要提前出去，完成路由跳转
      return newConversation.id;
    } catch (err) {
      this.setStatus(ChatStatus.DEFAULT);
    }
  };

  createQuestion = async (params: CreateChatParams) => {
    if (!checkLimit(params.spaceId)) {
      emitter.emit(EmitterEvent.chatAiLimit);
      throw new Error('ai limit exceeded');
    }

    if (this.getData().status !== ChatStatus.DEFAULT) {
      return;
    }

    this.cleanConversationEditData();
    this.setStatus(ChatStatus.CREATING);

    try {
      //这里要搞个假的，因为状态依赖于chatId，得先弄个假的让整个流程保持原样
      const { newQuestion, newChat } = createNewQuestion({
        ...params,
        conversationId: this.id,
        chatId: uuid(),
        newQuestionId: params.questionId ? undefined : uuid(),
      });

      const chatTools = new ChatTools(this.id, newQuestion.id, newChat.uuid);
      void chatTools.startStream(params);
      return newQuestion;
    } catch (err) {
      this.setStatus(ChatStatus.DEFAULT);
    }
  };

  stopAllChat = () => {
    this.setStatus(ChatStatus.DEFAULT);
    const questions = this.getQuestions();
    questions.forEach((question: QAQuestionDTO) => {
      question.chatList.forEach((chat: AIChatDTO) => {
        const chatTools = new ChatTools(this.id, question.id, chat.uuid);
        chatTools.stopChat();
      });
    });
    stopAllStream();
  };
}

export class QuestionTools {
  protected id: string;
  protected conversationId: string;
  protected readonly conversationTools: ConversationTools;

  constructor(id: string, conversationId: string) {
    this.id = id;
    this.conversationId = conversationId;
    this.conversationTools = new ConversationTools(conversationId);
  }

  /** 获取当前聊天的状态 */
  getQuestion = () => {
    const conversation = this.conversationTools.getConversation();
    if (!conversation) return getDefaultQuestion();

    const question = conversation.questions.find((q) => q.id === this.id);
    return question || getDefaultQuestion();
  };
}
export class ChatTools extends ReferenceTools {
  protected questionId: string;
  protected readonly conversationTools: ConversationTools;
  private readonly conversationId: string;

  constructor(conversationId: string, questionId: string, id: string) {
    super(id, (id: string, data: Record<string, any>) =>
      setUpdateChat(conversationId, questionId, id, data)
    );
    this.conversationId = conversationId;
    this.questionId = questionId;
    this.conversationTools = new ConversationTools(conversationId);
  }

  public getData = (): ReferenceData => {
    const defaultQaQueryOption = getDefaultConversationEditStore().qaQueryOption;
    const defaultData = {
      ...defaultQaQueryOption,
      status: getChatStatus(this.id),
    } as ReferenceData;

    const state = conversationStore.getState();
    const conversation = state[this.conversationId];
    if (!conversation) {
      return defaultData;
    }

    const currentQuestion = conversation.questions.find((q) => q.id === this.questionId);
    if (!currentQuestion) {
      return defaultData;
    }

    const currentChat = currentQuestion.chatList.find((c) => c.uuid === this.id);
    if (!currentChat) {
      return defaultData;
    }

    const { qaQueryOption } = currentChat;

    return {
      qaQueryOption: getQAQueryOption(qaQueryOption),
      status: getChatStatus(this.id),
    };
  };

  /** 更新聊天内容 */
  updateChat = (data: Partial<AIChatDTO> & { functionCall?: string }) => {
    setUpdateChat(this.conversationId, this.questionId, this.id, data);
  };

  /** 发送聊天消息 */
  startStream = async (params: CreateChatParams) => {
    const conversation = conversationStore.getState()[this.conversationId];
    //首次提问不需要滑到底部，否则会有个多余的过场动画
    if (conversation?.questions.length !== 1) {
      this.conversationTools.scrollToBottom();
    }
    const tryScrollBottom = throttle(() => {
      this.conversationTools.scrollToBottom();
    }, 300);
    this.setStatus(ChatStatus.LOADING);
    setIgnoreScrollBottom(false);
    let aiStreamed = '';
    let reasoningStreamed = '';
    let chunkChatId: string;
    let chunkQuestionId: string;
    let functionCall = '';
    let searchQueries: string[] = [];
    let searchResults: ResearchSearchItem[] = [];
    void streamAIResponse(
      () => {
        return this.id;
      },
      this.conversationId,
      params,
      (chunk) => {
        if (chunk.content) {
          aiStreamed += chunk.content;
          functionCall = '';
        }
        if (chunk.reasoning) {
          reasoningStreamed += chunk.reasoning;
          functionCall = '';
        }
        if (chunk.functionCall && chunk.functionCall !== functionCall) {
          functionCall = chunk.functionCall;
          if (functionCall === 'subpage_request' || functionCall === 'link_request') {
            functionCall = getI18n().t(functionCall);
          }
        }
        // 处理研究搜索查询和结果
        if (chunk.searchQueries) {
          searchQueries = chunk.searchQueries;
        }
        if (chunk.searchResults) {
          searchResults = chunk.searchResults;
        }
        if (chunk.chatId && chunk.questionId) {
          chunkChatId = chunk.chatId;
          chunkQuestionId = chunk.questionId;
          //去拉来源
          void API.ai.getChatResources(chunk.chatId).then((res) => {
            if (res.recordMap.blocks) {
              setAiChatPages(res.recordMap.blocks);
            }
            if (chunkChatId && chunkQuestionId) {
              exchangeStreams(this.id, chunkChatId);
              //替换为实际的questionId和chatId
              updateNewQuestionIdAndChatId(
                this.conversationId,
                this.questionId,
                this.id,
                chunkQuestionId,
                chunkChatId,
                res.resultData
              );
              this.id = chunkChatId;
              this.questionId = chunkQuestionId;
            }
            this.conversationTools.setStatus(ChatStatus.SENDING);
          });
        }
        //有时候读到的chunk有一百多个，处理不过来，state一直在刷新，所以这里加个requestAnimationFrame
        requestAnimationFrame(() => {
          this.updateChat({
            ai: aiStreamed,
            reasoning: reasoningStreamed,
            functionCall,
            searchQueries,
            searchResults,
          });
          tryScrollBottom();
        });
      }
    )
      .then(() => {
        this.updateChat({ status: ChatStatusType.SUCCESS });
      })
      .catch(() => {
        this.updateChat({ status: ChatStatusType.ERROR });
      })
      .finally(() => {
        void fetchAiLimit(params.spaceId);

        this.setStatus(ChatStatus.DEFAULT);
        this.conversationTools.setStatus(ChatStatus.DEFAULT);
        //恢复回去
        functionCall &&
          this.updateChat({
            functionCall: '',
          });
        setTimeout(() => {
          setIgnoreScrollBottom(false);
        }, 100);
      });
  };

  /** 停止消息流式响应 */
  stopChat = () => {
    this.setStatus(ChatStatus.DEFAULT);
  };

  setStatus = (status: ChatStatus) => {
    setChatStatus(this.id, status);
  };
}

const createNewConversation = async (params: CreateChatParams) => {
  const res = (await API.ai.createConversation({
    spaceId: params.spaceId,
    question: params.keywords,
    parentId: params.parentId,
  })) as {
    id: string;
    createdAt: number;
    updatedAt: number;
    question: string;
  };

  const newConversation: ConversationDetailDTO = {
    id: res.id,
    question: res.question ?? '',
    questions: [],
    createdAt: res.createdAt,
    updatedAt: res.updatedAt,
    parentId: params.parentId,
    spaceId: params.spaceId,
  };

  setConversationEdit(newConversation.id, params);
  setChatStatus(newConversation.id, ChatStatus.LOADING);

  setUpdateConversation(newConversation.id, newConversation);
  return newConversation;
};

const createNewQuestion = (
  params: CreateChatParams & {
    conversationId: string;
    chatId: string;
    newQuestionId?: string;
    resultData?: QASearchResultDTO;
    recordMap?: RecordInfoDTO;
  }
) => {
  if (params?.recordMap?.blocks) {
    setAiChatPages(params?.recordMap?.blocks);
  }

  let conversation = conversationStore.getState()[params.conversationId];
  const _question = conversation?.questions.find((q) => q.id === params.questionId);

  const newQuestion: QAQuestionDTO = _question ?? {
    id: params.newQuestionId ?? params.questionId ?? '',
    question: params.keywords,
    conversationId: params.conversationId,
    chatList: [],
    createdAt: Date.now(),
  };

  if (conversation) {
    // 如果传入的 newQuestionId，则添加新的问题。
    // 如果不存在，说明是重新回答
    if (params.newQuestionId) {
      addQuestion(params.conversationId, newQuestion);
    }
  }

  const newChat: AIChatDTO = {
    ...getDefaultChat(),
    uuid: params.chatId,
    ai: '',
    reasoning: '',
    user: params.keywords,
    createdAt: Date.now(),
    qaQueryOption: params.qaQueryOption,
    resultData: params.resultData,
    searchQueries: [],
    searchResults: [],
  };

  conversation = conversationStore.getState()[params.conversationId];
  if (conversation) {
    const question = conversation.questions.find((q) => q.id === newQuestion.id);
    if (question) {
      question.chatList.push(newChat);
      setUpdateQuestion(params.conversationId, newQuestion.id, { chatList: question.chatList });
    }
  }

  return {
    newQuestion,
    newChat,
  };
};
